import Decimal from 'decimal.js';
import { DateTime } from 'luxon';

import type { SimpleOrderLifetime } from 'components/OrderPanel/orderPanelTypes';
import type { Dollars, DollarsString } from 'models/dollars';
import { inversePriceDollars, parseDollarsString } from 'models/dollars';
import type { OrderAction, OrderStatus, OrderType, OrderVo } from 'models/order';
import type { UserOrderDTO } from 'models/ws';
import { toPriceInCents } from 'utils/caseing';
import { getEndOfDayExpirationTs } from 'utils/orders';

export type UserOrderLifetime = SimpleOrderLifetime | number;

export enum MatchingType {
    Immediate,
    Partial,
    Queued,
}

/**
 * Plain-data projection of a user's order, shared by REST hydration
 * (`OrderVo`) and the `user_orders` WS channel (`UserOrderDTO`). Derived
 * values live in the pure helpers below instead of getters, and dollar
 * amounts stay as `DollarsString` per the financial-math convention
 * (construct `Decimal` at the consumption site).
 */
export interface UserOrderEntry {
    id: string;
    userId: string;
    marketId: string;
    marketTicker: string;
    isYes: boolean;
    isSell: boolean;
    priceDollars: DollarsString;
    createTs: string;
    /** `updated_ts`, falling back to `create_ts` for never-updated orders. */
    lastUpdateTs: string;
    expirationTs: string | null;
    status: OrderStatus | null;
    orderAction: OrderAction;
    orderType: OrderType | null;
    /** Contracts filled so far (fixed-point aware). */
    fillCount: number;
    /** Contracts still resting on the book (fixed-point aware). */
    remainingCount: number;
    initialCount: number;
    /** Cents. */
    takerFillCost: number;
    makerFillCost: number;
    takerFillFees: number;
    makerFillFees: number;
}

function centsFromDollarsOrCents(dollars: DollarsString | undefined, cents: number | undefined): number {
    return dollars !== undefined ? parseDollarsString(dollars).times(100).toNumber() : (cents ?? 0);
}

function countFromFixedPoint(fp: string | undefined, count: number): number {
    return fp ? parseFloat(fp) : count;
}

/**
 * `order_action` is optional on `OrderVo` — some REST hydration paths (amend
 * success, modal reload) omit it. When absent, fall back to whatever side is
 * already cached for this order instead of defaulting to a buy, mirroring
 * `userOrderEntryFromWsDto`'s handling of the same gap.
 */
export function userOrderEntryFromRestVo(vo: OrderVo, existing?: UserOrderEntry): UserOrderEntry {
    return {
        id: vo.order_id,
        userId: vo.user_id,
        marketId: vo.market_id,
        marketTicker: vo.market_ticker,
        isYes: vo.is_yes,
        isSell: vo.order_action ? vo.order_action === 'sell' : (existing?.isSell ?? false),
        priceDollars: vo.price_dollars,
        createTs: vo.create_ts,
        lastUpdateTs: vo.updated_ts ?? vo.create_ts,
        expirationTs: vo.expiration_ts,
        status: vo.status,
        orderAction: vo.order_action ?? existing?.orderAction ?? null,
        orderType: vo.order_type,
        fillCount: countFromFixedPoint(vo.fill_count_fp, vo.fill_count),
        remainingCount: countFromFixedPoint(vo.remaining_count_fp, vo.remaining_count),
        initialCount: countFromFixedPoint(vo.initial_count_fp, vo.initial_count),
        takerFillCost: centsFromDollarsOrCents(vo.taker_fill_cost_dollars, vo.taker_fill_cost),
        makerFillCost: vo.maker_fill_cost ?? 0,
        takerFillFees: centsFromDollarsOrCents(vo.taker_fees_dollars, vo.taker_fees),
        makerFillFees: centsFromDollarsOrCents(vo.maker_fees_dollars, vo.maker_fees),
    };
}

/**
 * Build the next entry for a `user_order` WS message, merged over whatever is
 * already cached. Mirrors the old `Order.updateFromWsDTO` semantics: identity
 * fields and `createTs` are only filled when the cached entry lacks them, and
 * `orderAction`/`isSell` are preserved when the DTO omits `order_action`.
 */
export function userOrderEntryFromWsDto(dto: UserOrderDTO, existing?: UserOrderEntry): UserOrderEntry {
    return {
        id: existing?.id || dto.order_id,
        userId: existing?.userId || dto.user_id,
        marketId: existing?.marketId || dto.market_id,
        marketTicker: existing?.marketTicker || dto.market_ticker,
        isYes: dto.is_yes,
        isSell: dto.order_action ? dto.order_action === 'sell' : (existing?.isSell ?? false),
        priceDollars: dto.price_dollars,
        createTs: existing?.createTs || dto.create_ts,
        lastUpdateTs: dto.updated_ts ?? dto.create_ts,
        expirationTs: dto.expiration_ts,
        status: dto.status,
        orderAction: dto.order_action ?? existing?.orderAction ?? null,
        orderType: existing?.orderType ?? null,
        fillCount: countFromFixedPoint(dto.fill_count_fp, dto.fill_count || (existing?.fillCount ?? 0)),
        remainingCount: countFromFixedPoint(dto.remaining_count_fp, dto.remaining_count),
        initialCount: countFromFixedPoint(dto.initial_count_fp, dto.initial_count),
        takerFillCost: centsFromDollarsOrCents(dto.taker_fill_cost_dollars, dto.taker_fill_cost),
        makerFillCost: dto.maker_fill_cost ?? 0,
        takerFillFees: centsFromDollarsOrCents(dto.taker_fees_dollars, dto.taker_fees),
        makerFillFees: centsFromDollarsOrCents(dto.maker_fees_dollars, dto.maker_fees),
    };
}

export function userOrderPriceDollars(order: UserOrderEntry): Dollars {
    return parseDollarsString(order.priceDollars);
}

/** Order price in cents. */
export function userOrderPriceCents(order: UserOrderEntry): number {
    return userOrderPriceDollars(order).times(100).toNumber();
}

export function isUserOrderPending(order: UserOrderEntry): boolean {
    return order.status === 'pending';
}

export function userOrderPriceFromActionPerspectiveValue(order: UserOrderEntry): Dollars {
    const priceDollars = userOrderPriceDollars(order);
    return order.isSell ? inversePriceDollars(priceDollars) : priceDollars;
}

export function userOrderPriceFromActionPerspective(order: UserOrderEntry): string {
    return toPriceInCents(userOrderPriceFromActionPerspectiveValue(order));
}

export function userOrderActionAndSide(order: UserOrderEntry): string {
    return order.isSell ? (order.isYes ? 'Sold No' : 'Sold Yes') : order.isYes ? 'Bought Yes' : 'Bought No';
}

export function userOrderActionAndSideResting(order: UserOrderEntry): string {
    return order.isSell ? (order.isYes ? 'Sell No' : 'Sell Yes') : order.isYes ? 'Buy Yes' : 'Buy No';
}

export function userOrderFillFees(order: UserOrderEntry): number {
    return order.takerFillFees + order.makerFillFees;
}

/** Absolute fill cost including fees, in cents. */
export function userOrderFillCostWithFees(order: UserOrderEntry): number {
    const costInCents = order.makerFillCost + order.makerFillFees + order.takerFillCost + order.takerFillFees;
    return Math.abs(order.isSell ? costInCents - order.fillCount * 100 : costInCents);
}

export function userOrderLifetime(order: UserOrderEntry): UserOrderLifetime {
    return order.expirationTs === null
        ? 'indefinite'
        : DateTime.fromISO(order.expirationTs).toSeconds() === -1
          ? 'IOC'
          : +DateTime.fromSeconds(getEndOfDayExpirationTs()) === +DateTime.fromISO(order.expirationTs)
            ? 'EOD'
            : DateTime.fromISO(order.expirationTs).toSeconds();
}

/**
 * Resting filter matching the old `OrderStore.restingOrders` getter: any
 * order with contracts still on the book (not `status === 'resting'`, which
 * is ambiguous for partial fills), newest first.
 */
export function selectRestingUserOrders(orders: ReadonlyMap<string, UserOrderEntry>): UserOrderEntry[] {
    const resting: UserOrderEntry[] = [];
    orders.forEach((order) => {
        if (order.remainingCount > 0) resting.push(order);
    });
    return resting.sort((a, b) => {
        const da = DateTime.fromISO(a.createTs);
        const db = DateTime.fromISO(b.createTs);
        return da !== db ? (da > db ? -1 : 1) : 0;
    });
}

export function selectRestingUserOrdersInMarket(
    orders: ReadonlyMap<string, UserOrderEntry>,
    marketId: string,
): UserOrderEntry[] {
    return selectRestingUserOrders(orders).filter((order) => order.marketId === marketId);
}

export interface PersonalOrderbookEntry {
    yesQuantity: number;
    noQuantity: number;
}
export type PersonalOrderbook = Array<PersonalOrderbookEntry>;
export type PersonalOrderbookMap = Map<string, PersonalOrderbookEntry>;

/** Per-price aggregation of the user's own resting contracts in one market. */
export function buildPersonalOrderbookMap(
    orders: ReadonlyMap<string, UserOrderEntry>,
    marketId: string,
    collapseToCents: boolean,
): PersonalOrderbookMap {
    const personalOrderbook = new Map<string, PersonalOrderbookEntry>();
    orders.forEach((order) => {
        if (order.marketId !== marketId || order.remainingCount <= 0) return;
        const priceDollars = userOrderPriceDollars(order);
        const key = order.isYes
            ? collapseToCents
                ? priceDollars.times(100).floor().dividedBy(100).toString()
                : priceDollars.toString()
            : new Decimal(1).minus(priceDollars).toString();
        let entry = personalOrderbook.get(key);
        if (!entry) {
            entry = { yesQuantity: 0, noQuantity: 0 };
            personalOrderbook.set(key, entry);
        }
        if (order.isYes) {
            entry.yesQuantity += order.remainingCount;
        } else {
            entry.noQuantity += order.remainingCount;
        }
    });
    return personalOrderbook;
}
